import { useState, useEffect } from 'react';
import { NotificationUser, NotificationUserFormData } from '../models/notificationUser';
import { fetchNotificationUsers, createNotificationUser, deleteNotificationUser } from '../api/notificationUser';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { getUserProfile } from '../configuration/login/loginSlice';
import { fetchUserId } from '../api/fetchUser';

export const useNotificationUsers = () => {
  const userProfile = useSelector(getUserProfile);
  const intl = useIntl();

  const [notificationUsers, setNotificationUsers] = useState<NotificationUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [showForm, setShowForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [notificationUserToDelete, setNotificationUserToDelete] = useState<NotificationUser | null>(null);

  const [userHasAccess, setUserHasAccess] = useState<boolean | null>(null);

  const loadNotificationUsers = async (page: number = 1) => {
    setLoading(true);
    setError(null);
    try {
      const userId = await fetchUserId(userProfile);
      if (!userId) {
        setUserHasAccess(false);
        return;
      }

      const authorizedUserIds = import.meta.env.VITE_BACKOFFICE_AUTHORIZED_USER_IDS?.split(',').map((id: string) => id.trim()) || [];
      const isAuthorized = authorizedUserIds.includes(userId);
      if (!isAuthorized) {
        setUserHasAccess(false);
        return;
      }

      setUserHasAccess(true);

      const data = await fetchNotificationUsers({ page, limit: pageSize });
      setNotificationUsers([...data.users]);
      setTotalElements(data.totalElements);
      setTotalPages(data.totalPages);
      setCurrentPage(data.currentPage);
    } catch (err) {
      setError(intl.formatMessage({ id: 'intl-msg:backoffice.notificationUserList.loadError' }));
      console.error('Error loading notification users:', err);
    } finally {
      setLoading(false);
    }
  };

  const addNotificationUser = async (data: NotificationUserFormData): Promise<boolean> => {
    setError(null);
    
    try {
      const success = await createNotificationUser(data);
      if (success) {
        await loadNotificationUsers(currentPage);
        return true;
      }
      return false;
    } catch (err) {
      setError(intl.formatMessage({ id: 'intl-msg:backoffice.useNotificationUsers.createError' }));
      console.error('Error creating notification user:', err);
      return false;
    }
  };

  const removeNotificationUser = async (id: string): Promise<boolean> => {
    setError(null);
    setDeleteLoading(true);
    try {
      const success = await deleteNotificationUser(id);
      if (success) {
        // Se a página atual ficar vazia e não for a primeira, voltar uma página
        const remainingUsers = notificationUsers.filter(user => user.id !== id);
        if (remainingUsers.length === 0 && currentPage > 1) {
          await loadNotificationUsers(currentPage - 1);
        } else {
          await loadNotificationUsers(currentPage);
        }
        return true;
      }
      return false;
    } catch (err) {
      setError(intl.formatMessage({ id: 'intl-msg:backoffice.useNotificationUsers.deleteError' }));
      console.error('Error deleting notification user:', err);
      return false;
    } finally {
      setDeleteLoading(false);
    }
  };

  useEffect(() => {
    loadNotificationUsers(1);
  }, []);

  const handleDelete = (notificationUser: NotificationUser) => {
    setNotificationUserToDelete(notificationUser);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (notificationUserToDelete?.id) {
      await removeNotificationUser(notificationUserToDelete.id);
      setShowDeleteDialog(false);
      setNotificationUserToDelete(null);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
  };

  const handleFormSuccess = () => {
    setShowForm(false);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      loadNotificationUsers(page);
    }
  };

  return {
    notificationUsers,
    loading,
    deleteLoading,
    error,
    loadNotificationUsers,
    addNotificationUser,
    removeNotificationUser,
    showForm,
    showDeleteDialog,
    notificationUserToDelete,
    handleDelete,
    confirmDelete,
    handleFormClose,
    handleFormSuccess,
    setShowForm,
    setShowDeleteDialog,
    userHasAccess,
    currentPage,
    totalPages,
    totalElements,
    pageSize,
    handlePageChange,
  };
};

